/**
 * Master admin: gerencia RoleGroups (perfis compostos que empacotam N Roles).
 *
 * Lista paginada server-side. Cada grupo pode ser:
 *   - Global (institutionId = null): template do produto, disponível para
 *     qualquer instituição usar.
 *   - Por instituição: customizado daquela organização.
 *
 * Dialog de criação/edição:
 *   - Nome + descrição
 *   - Escopo: Global ou (futuro) instituição específica
 *   - Multi-select de Roles disponíveis (catalogadas por módulo)
 *
 * Endpoints:
 *   GET    /api/role-groups?page&page_size&q&scope&filter[..][..]
 *   GET    /api/role-groups/:id
 *   POST   /api/role-groups
 *   PATCH  /api/role-groups/:id
 *   DELETE /api/role-groups/:id
 */

import { useState } from "react";
import {
  Box, Button, Chip, Stack, Typography, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Autocomplete, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PublicIcon from "@mui/icons-material/Public";
import BusinessIcon from "@mui/icons-material/Business";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GridColDef } from "@mui/x-data-grid";
import { ConfirmDialog, ServerDataGrid, formatRelativeFromNow, useDataGridState } from "@safrater/ui";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";

interface RoleGroupRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  institutionId: string | null;
  institutionName: string | null;
  roleCount: number;
  memberCount: number;
  createdAt: string;
}

interface RoleCatalog {
  id: string; key: string; name: string;
  module: { id: string; key: string; name: string };
}

interface RoleGroupDetail extends RoleGroupRow {
  roles: { role: RoleCatalog }[];
}

interface DlgState {
  open: boolean;
  mode: "create" | "edit";
  row?: RoleGroupRow;
}

export function RoleGroupsPage() {
  const state = useDataGridState();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [dlg, setDlg] = useState<DlgState>({ open: false, mode: "create" });
  const [confirmDelete, setConfirmDelete] = useState<RoleGroupRow | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["role-groups", state.queryParams],
    queryFn: async () => (await api.get<{ data: RoleGroupRow[]; total: number }>("/role-groups", { params: state.queryParams })).data,
    placeholderData: (prev) => prev,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["role-groups"] });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/role-groups/${id}`),
    onSuccess: () => { invalidate(); setConfirmDelete(null); },
  });

  const columns: GridColDef<RoleGroupRow>[] = [
    {
      field: "name", headerName: "Nome", flex: 1, minWidth: 220,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          {row.institutionId ? <BusinessIcon fontSize="small" color="info" /> : <PublicIcon fontSize="small" color="primary" />}
          <Box>
            <Typography variant="body2">{row.name}</Typography>
            <Typography variant="caption" color="text.secondary">{row.key}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: "institutionName", headerName: "Escopo", flex: 1, minWidth: 180,
      valueGetter: (_, row) => row.institutionName ?? "Global",
    },
    {
      field: "roleCount", headerName: "Roles", width: 100, type: "number",
      renderCell: ({ value }) => <Chip size="small" label={value} variant="outlined" />,
    },
    {
      field: "memberCount", headerName: "Atribuído a", width: 130, type: "number",
      renderCell: ({ value }) => <Chip size="small" label={`${value} user${value === 1 ? "" : "s"}`} color={value > 0 ? "success" : "default"} variant="outlined" />,
    },
    { field: "createdAt", headerName: "Criado", width: 160, valueFormatter: (v) => formatRelativeFromNow(v as string) },
    {
      field: "_actions", headerName: "Ações", width: 110, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar">
            <IconButton size="small" aria-label="Editar" onClick={() => setDlg({ open: true, mode: "edit", row })}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" color="error" aria-label="Excluir" onClick={() => setConfirmDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h4">Grupos de papéis</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg({ open: true, mode: "create" })}>
          Novo grupo
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Empacote várias roles (de qualquer módulo) num único pacote e atribua de uma só vez ao usuário.
        Grupos <strong>globais</strong> servem como template do produto; grupos por instituição são exclusivos daquela organização.
      </Typography>

      <Box sx={{ height: 640 }}>
        <ServerDataGrid<RoleGroupRow>
          gridId="central.role-groups"
          userScope={user?.sub}
          rows={data?.data ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          paginationModel={state.pagination}
          onPaginationModelChange={state.setPagination}
          sortModel={state.sort}
          onSortModelChange={state.setSort}
          filterModel={state.filter}
          onFilterModelChange={state.setFilter}
          defaultColumnVisibility={{ createdAt: false }}
        />
      </Box>

      {dlg.open && (
        <RoleGroupDialog
          mode={dlg.mode}
          rowId={dlg.row?.id}
          onClose={() => setDlg({ open: false, mode: "create" })}
          onSaved={() => { invalidate(); setDlg({ open: false, mode: "create" }); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir grupo"
        description={`Tem certeza que deseja excluir o grupo "${confirmDelete?.name}"? Usuários que tinham este grupo perderão as roles vindas dele.`}
        confirmLabel="Excluir"
        severity="error"
        loading={deleteM.isPending}
        onConfirm={() => { if (confirmDelete) deleteM.mutate(confirmDelete.id); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  );
}

// ─── Dialog create/edit ──────────────────────────────────────────────────

interface RoleGroupDialogProps {
  mode: "create" | "edit";
  rowId?: string;
  onClose: () => void;
  onSaved: () => void;
}

function RoleGroupDialog({ mode, rowId, onClose, onSaved }: RoleGroupDialogProps) {
  // Detalhe atual (se edit) com roles incluídas.
  const detailQ = useQuery({
    queryKey: ["role-group", rowId],
    queryFn: async () => (await api.get<RoleGroupDetail>(`/role-groups/${rowId}`)).data,
    enabled: mode === "edit" && !!rowId,
  });

  // Catálogo de Roles (todos os módulos).
  const rolesQ = useQuery<RoleCatalog[]>({
    queryKey: ["roles-all-for-group"],
    queryFn: async () => {
      const mods = (await api.get<{ id: string; active?: boolean }[]>("/modules")).data ?? [];
      const all: RoleCatalog[] = [];
      for (const md of mods.filter((m) => m.active !== false)) {
        const rs = await api.get<RoleCatalog[]>("/roles", { params: { module_id: md.id } });
        all.push(...rs.data);
      }
      return all;
    },
  });

  // Instituições para escolher o escopo do grupo (Global ou local de uma instituição).
  const institutionsQ = useQuery<{ id: string; name: string }[]>({
    queryKey: ["institutions-lite-rg"],
    queryFn: async () => (await api.get<{ data: { id: string; name: string }[] }>("/institutions", { params: { page_size: 200 } })).data.data,
    enabled: mode === "create",
  });

  const [values, setValues] = useState<{ key: string; name: string; description: string; roleIds: string[]; institutionId: string }>({
    key: "", name: "", description: "", roleIds: [], institutionId: "",
  });
  const [initialized, setInitialized] = useState(false);

  // Hidrata o form com o detalhe quando carregar.
  if (mode === "edit" && detailQ.data && !initialized) {
    setValues({
      key: detailQ.data.key,
      name: detailQ.data.name,
      description: detailQ.data.description ?? "",
      roleIds: detailQ.data.roles.map((r) => r.role.id),
      institutionId: detailQ.data.institutionId ?? "",
    });
    setInitialized(true);
  }

  const selectedRoles = (rolesQ.data ?? []).filter((r) => values.roleIds.includes(r.id));

  const saveM = useMutation({
    mutationFn: (): Promise<unknown> => {
      if (mode === "create") {
        return api.post("/role-groups", {
          key: values.key, name: values.name,
          description: values.description || undefined,
          institutionId: values.institutionId || undefined,
          roleIds: values.roleIds,
        });
      }
      return api.patch(`/role-groups/${rowId}`, {
        name: values.name,
        description: values.description || undefined,
        roleIds: values.roleIds,
      });
    },
    onSuccess: onSaved,
  });

  const canSave = values.name.trim().length > 0 && values.roleIds.length > 0 &&
    (mode === "edit" || /^[a-z0-9-]+$/.test(values.key));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === "create" ? "Novo grupo" : `Editar: ${detailQ.data?.name ?? "..."}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {saveM.error && <Alert severity="error">Não foi possível salvar.</Alert>}
          {mode === "create" && (
            <>
              <TextField
                label="Chave (slug kebab)" required autoFocus
                value={values.key}
                onChange={(e) => setValues({ ...values, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                helperText="Apenas a-z, 0-9 e hífen. Ex.: gestor-rh-completo"
              />
              <TextField
                select label="Escopo" value={values.institutionId}
                onChange={(e) => setValues({ ...values, institutionId: e.target.value })}
                helperText="Global (template do produto) ou exclusivo de uma instituição."
              >
                <MenuItem value="">Global (todas as instituições)</MenuItem>
                {(institutionsQ.data ?? []).map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
              </TextField>
            </>
          )}
          <TextField label="Nome" required autoFocus={mode === "edit"} value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })} />
          <TextField label="Descrição" multiline rows={2}
            value={values.description}
            onChange={(e) => setValues({ ...values, description: e.target.value })} />
          <Autocomplete<RoleCatalog, true>
            multiple
            loading={rolesQ.isFetching}
            options={rolesQ.data ?? []}
            value={selectedRoles}
            onChange={(_, v) => setValues({ ...values, roleIds: v.map((r) => r.id) })}
            groupBy={(o) => o.module.name}
            getOptionLabel={(o) => `${o.module.key} / ${o.name}`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={`${option.module.key} / ${option.name}`}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            renderInput={(p) => <TextField {...p} label="Roles a compor (escolha de qualquer módulo)" />}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!canSave || saveM.isPending} onClick={() => saveM.mutate()}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
